import React, { useEffect, forwardRef, Component, RefObject, createRef, ClassAttributes, useRef } from 'react';
import { CustomObject } from '../../../types/data';
import { useSpacePhotoTexture } from '../../../hooks';
import { BackSide, Material, MathUtils, Mesh, MeshBasicMaterial, Texture } from 'three';
import { ReactThreeFiber } from 'react-three-fiber';
import { isArray, isFunction } from 'lodash';
import anime, { AnimeInstance } from 'animejs';

const LoaderSpaceContent = forwardRef<
  Mesh,
  {
    data: CustomObject;
    radius: number;
    renderOrder?: number;
    rotation?: ReactThreeFiber.Euler;
    onLoad: () => void;
  }
>(({ data, radius, onLoad, ...props }, ref) => {
  const { loaded, texture } = useSpacePhotoTexture({
    type: 'pano',
    source: data.video_thumbnail_url,
  });
  const matRef = useRef<Material>();

  useEffect(() => {
    if (!matRef.current) return;
    matRef.current.opacity = 1;
  }, [data.id]);

  useEffect(() => {
    if (loaded && isFunction(onLoad)) onLoad();
  }, [loaded, onLoad]);

  return (
    <mesh ref={ref} {...props}>
      <sphereGeometry attach="geometry" args={[radius, 32, 32]} />
      <meshBasicMaterial attach="material" side={BackSide} transparent ref={matRef}>
        <primitive attach="map" object={texture} />
      </meshBasicMaterial>
    </mesh>
  );
});

const LoaderCubeSpaceContent = forwardRef<
  Mesh,
  {
    data: CustomObject;
    radius: number;
    renderOrder?: number;
    rotation?: ReactThreeFiber.Euler;
    onLoad: () => void;
  }
>(({ data, radius, onLoad, ...props }, ref) => {
  const { loaded, texture } = useSpacePhotoTexture({
    type: 'multi',
    source: data.urls,
  });

  useEffect(() => {
    if (loaded && isFunction(onLoad)) onLoad();
  }, [loaded, onLoad]);

  return (
    <group scale={[-1, 1, 1]}>
      <mesh
        ref={ref}
        {...props}
        material={(texture as Texture[]).map((txt, i) => {
          if (i === 2 || i === 3) {
            txt.center.set(0.5, 0.5);
            txt.rotation = MathUtils.degToRad(180);
          }

          const mat = new MeshBasicMaterial({
            map: txt,
            side: BackSide,
            transparent: true,
            opacity: 1,
          });
          return mat;
        })}>
        <boxGeometry args={[radius, radius, radius]} />
      </mesh>
    </group>
  );
});

type TLoaderSpaceProps = ClassAttributes<{}> & {
  data: CustomObject;
  radius: number;
  rotation?: ReactThreeFiber.Euler;
  renderOrder?: number;
  enableAnimation?: boolean;
  resourceId?: string;
  onLoadStart?: () => void;
  onLoad?: () => void;
  onAnimComplete?: () => void;
};

type TLoaderSpaceState = {
  contentData?: {
    id: number;
    urls: string[] | undefined;
    video_thumbnail_url: string | undefined;
  };
};

class LoaderSpace extends Component<TLoaderSpaceProps, TLoaderSpaceState> {
  private animeInstance: AnimeInstance;
  private meshRef: RefObject<Mesh>;
  private animParams: {
    opacity: number;
  };

  static getDerivedStateFromProps(props: TLoaderSpaceProps, state: TLoaderSpaceState) {
    const { data, onLoadStart } = props;
    const cleanData = { ...data };
    delete cleanData.isPhoto;

    if ((data.box || data.video_thumbnail_url) && data.id !== state.contentData?.id) {
      if (isFunction(onLoadStart)) {
        onLoadStart();
      }
      return {
        ...state,
        contentData: {
          id: data.id,
          urls: data.box
            ? [data.box['l'], data.box['r'], data.box['u'], data.box['d'], data.box['b'], data.box['f']]
            : [],
          video_thumbnail_url: data.video_thumbnail_url,
        },
      };
    }
    return null;
  }

  constructor(props: Readonly<TLoaderSpaceProps>) {
    super(props);

    this.state = {
      contentData: undefined,
    };

    this.meshRef = createRef();

    this.animParams = {
      opacity: 1,
    };

    this.animeInstance = this.createAnimInstance();
  }

  render() {
    const { radius, renderOrder, rotation, data } = this.props;
    const { contentData } = this.state;
    const type = data.video_thumbnail_url ? 'pano' : 'cube';
    const Component = type === 'cube' ? LoaderCubeSpaceContent : LoaderSpaceContent;

    if (contentData)
      return (
        <Component
          data={contentData}
          radius={radius}
          renderOrder={renderOrder}
          rotation={rotation}
          onLoad={this.handleTextureLoad}
          ref={this.meshRef}
        />
      );

    return null;
  }

  private handleTextureLoad = () => {
    const { onLoad } = this.props;

    if (isFunction(onLoad)) {
      onLoad();
    }
  };

  private createAnimInstance = () => {
    const duration = 1000;

    this.animParams = {
      opacity: 1,
    };

    const animeInstance = anime({
      targets: this.animParams,
      /// animated props
      opacity: 0,
      /// end animated props
      loop: false,
      autoplay: false,
      duration,
      easing: 'linear',
      update: this.animUpdate,
      complete: this.animComplete,
    });

    return animeInstance;
  };

  public animReset = () => {
    this.animeInstance?.pause();
    this.animeInstance = this.createAnimInstance();
  };

  public animPlay = () => {
    if (this.props.enableAnimation) {
      this.animeInstance.play();
    } else {
      this.updateMaterial(0);
      this.animComplete();
    }
  };

  private updateMaterial(opacity: number) {
    if (!this.meshRef.current) return;

    const { material } = this.meshRef.current;

    if (isArray(material)) {
      material.forEach(mt => (mt.opacity = opacity));
    } else {
      material.opacity = opacity;
    }
  }

  /// animation callbacks
  private animUpdate = () => {
    const { opacity } = this.animParams;
    this.updateMaterial(opacity);
  };

  private animComplete = () => {
    const { onAnimComplete } = this.props;

    if (isFunction(onAnimComplete)) {
      onAnimComplete();
    }

    // setTimeout(() => {
    //   if (!this.meshRef.current) return;

    //   const { material } = this.meshRef.current;

    //   if (isArray(material)) {
    //     material.forEach(mt => ((mt as any).map = undefined));
    //   } else {
    //     (material as any).map = undefined;
    //   }
    // }, 1000);
  };
}

export default LoaderSpace;
